<template>
  <div class="cs-text-editor">
    <div v-if="editor" class="text-editor-menu">
      <button
        type="button"
        title="Paragraph"
        @click="
          editor
            .chain()
            .focus()
            .setParagraph()
            .run()
        "
        :class="{ 'is-active': editor.isActive('paragraph') }"
      >
        <icon name="paragraph" />
      </button>
      <button
        type="button"
        title="Bold"
        @click="
          editor
            .chain()
            .focus()
            .toggleBold()
            .run()
        "
        :class="{ 'is-active': editor.isActive('bold') }"
      >
        <icon name="bold" />
      </button>
      <button
        type="button"
        title="Italic"
        @click="
          editor
            .chain()
            .focus()
            .toggleItalic()
            .run()
        "
        :class="{ 'is-active': editor.isActive('italic') }"
      >
        <icon name="italic" />
      </button>
      <button
        type="button"
        title="Strike-through"
        @click="
          editor
            .chain()
            .focus()
            .toggleStrike()
            .run()
        "
        :class="{ 'is-active': editor.isActive('strike') }"
      >
        <icon name="strikethrough" />
      </button>
      <button
        type="button"
        title="Unordered List"
        @click="
          editor
            .chain()
            .focus()
            .toggleBulletList()
            .run()
        "
        :class="{ 'is-active': editor.isActive('bulletList') }"
      >
        <icon name="ul" />
      </button>
      <button
        type="button"
        title="Link"
        @click="setLink"
        :class="{ 'is-active': editor.isActive('link') }"
      >
        <icon name="link" />
      </button>
      <button
        type="button"
        title="Heading 1"
        @click="
          editor
            .chain()
            .focus()
            .toggleHeading({ level: 1 })
            .run()
        "
        :class="{ 'is-active': editor.isActive('heading', { level: 1 }) }"
      >
        <icon name="h1" />
      </button>
      <button
        type="button"
        title="Heading 2"
        @click="
          editor
            .chain()
            .focus()
            .toggleHeading({ level: 2 })
            .run()
        "
        :class="{ 'is-active': editor.isActive('heading', { level: 2 }) }"
      >
        <icon name="h2" />
      </button>
      <button
        type="button"
        title="Heading 3"
        @click="
          editor
            .chain()
            .focus()
            .toggleHeading({ level: 3 })
            .run()
        "
        :class="{ 'is-active': editor.isActive('heading', { level: 3 }) }"
      >
        <icon name="h3" />
      </button>
      <button
        type="button"
        title="Clear Formatting"
        @click="
          editor
            .chain()
            .focus()
            .unsetAllMarks()
            .run()
        "
      >
        <icon name="format-clear" />
      </button>
    </div>
    <editor-content :editor="editor" />
  </div>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-2";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Mention from "@tiptap/extension-mention";
import Icon from "./Icon.vue";
import suggestion from "./suggestion";

export default {
  name: "Editor",

  components: {
    EditorContent,
    Icon
  },

  props: {
    value: {
      type: String,
      default: ""
    }
  },

  data() {
    return {
      editor: null
    };
  },

  watch: {
    value(value) {
      const isSame = this.editor.getHTML() === value;

      if (isSame) {
        return;
      }

      this.editor.commands.setContent(value, false);
    }
  },

  methods: {
    setLink() {
      const previousUrl = this.editor.getAttributes("link").href;
      const url = window.prompt("URL", previousUrl);

      // cancelled
      if (url === null) {
        return;
      }

      // empty
      if (url === "") {
        this.editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .unsetLink()
          .run();

        return;
      }

      // update link
      this.editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: url })
        .run();
    }
  },

  mounted() {
    this.editor = new Editor({
      extensions: [
        StarterKit,
        Link.configure({
          openOnClick: false
        }),
        Mention.configure({
          HTMLAttributes: {
            class: "mention"
          },
          renderLabel({ options, node }) {
            return `${options.suggestion.char}${node.attrs.label ??
              node.attrs.id}`;
          },
          suggestion
        })
      ],
      content: this.value,
      onUpdate: () => {
        this.$emit("input", this.editor.getHTML());
      }
    });
  },

  beforeUnmount() {
    this.editor.destroy();
  }
};
</script>
<style lang="scss">
@import "@/assets/scss/md/_variables.scss";

/* Basic editor styles */
.cs-text-editor {
  .text-editor-menu {
    background-color: #c2d5da;
    border-radius: 7px 7px 0 0;
    padding: 5px;

    button {
      width: 24px;
      height: 24px;
      padding: 2px;
    }
  }

  .mention {
    background-color: $brand-primary;
    color: white;
    border-radius: 1rem;
    padding: 0.2rem 0.5rem;
    box-decoration-break: clone;
    margin-right: 5px;
  }

  .ProseMirror {
    padding: 15px;
    border: 1px solid #ddd;
    border-radius: 0 0 7px 7px;
    background-color: #f6f6f6;
    min-height: 250px;
    max-height: 320px;
    overflow-y: scroll;

    > * + * {
      margin-top: 0.5em;
    }

    button {
      width: 24px;
      height: 24px;
      padding: 2px;
    }

    code {
      background-color: rgba(#616161, 0.1);
      color: #616161;
    }

    h1 {
      font-size: 1.7em;
      font-weight: bold;
      color: $brand-primary;
    }

    h2 {
      font-size: 1.5em;
      font-weight: bold;
      color: $brand-primary;
    }

    h3 {
      font-size: 1.2em;
      font-weight: bold;
      text-transform: uppercase;
      color: $brand-primary;
    }
  }

  .is-active {
    background-color: $brand-primary;
    color: #fff;
  }
}
</style>
